import { useState, useCallback, useMemo } from "react";
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { MapPin } from "lucide-react";

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";

interface Business {
  id: string;
  business_name: string;
  category: string;
  address: string;
  state?: string | null;
  postcode?: string | null;
  latitude: number | null;
  longitude: number | null;
  description?: string | null;
}

interface BusinessMapViewProps {
  businesses: Business[];
  center?: { lat: number; lng: number };
  className?: string;
}

const containerStyle = {
  width: "100%",
  height: "100%",
};

const defaultCenter = {
  lat: 14.5995,
  lng: 120.9842,
};

const categoryLabels: Record<string, string> = {
  spa_wellness: "Spa & Wellness",
  massage_therapy: "Massage Therapy",
  fitness_gym: "Fitness & Gym",
  yoga_meditation: "Yoga & Meditation",
  beauty_salon: "Beauty & Salon",
  health_coaching: "Health Coaching",
};

function BusinessMapViewInner({
  businesses,
  center,
  apiKey,
}: BusinessMapViewProps & { apiKey: string }) {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: apiKey,
  });

  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);

  const businessesWithLocation = useMemo(
    () => businesses.filter((b) => b.latitude && b.longitude),
    [businesses]
  );

  const mapCenter = useMemo(() => {
    if (center) return center;
    if (businessesWithLocation.length > 0) {
      const avgLat =
        businessesWithLocation.reduce((sum, b) => sum + (b.latitude || 0), 0) /
        businessesWithLocation.length;
      const avgLng =
        businessesWithLocation.reduce((sum, b) => sum + (b.longitude || 0), 0) /
        businessesWithLocation.length;
      return { lat: avgLat, lng: avgLng };
    }
    return defaultCenter;
  }, [businessesWithLocation, center]);

  const onMarkerClick = useCallback((business: Business) => {
    setSelectedBusiness(business);
  }, []);

  if (!isLoaded) {
    return (
      <div className="h-full bg-muted rounded-xl animate-pulse flex items-center justify-center">
        <MapPin className="w-8 h-8 text-muted-foreground" />
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={mapCenter}
      zoom={12}
      options={{
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: true,
      }}
    >
      {businessesWithLocation.map((business) => (
        <Marker
          key={business.id}
          position={{ lat: business.latitude!, lng: business.longitude! }}
          onClick={() => onMarkerClick(business)}
        />
      ))}

      {selectedBusiness && selectedBusiness.latitude && selectedBusiness.longitude && (
        <InfoWindow
          position={{
            lat: selectedBusiness.latitude,
            lng: selectedBusiness.longitude,
          }}
          onCloseClick={() => setSelectedBusiness(null)}
        >
          <Link to={`/provider/${selectedBusiness.id}`} className="block max-w-[200px]">
            <h3 className="font-semibold text-foreground text-sm mb-1">
              {selectedBusiness.business_name}
            </h3>
            <Badge variant="secondary" className="text-xs mb-2">
              {categoryLabels[selectedBusiness.category] || selectedBusiness.category}
            </Badge>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="w-3 h-3" />
              <span className="line-clamp-1">{[selectedBusiness.address, selectedBusiness.state, selectedBusiness.postcode].filter(Boolean).join(', ')}</span>
            </div>
          </Link>
        </InfoWindow>
      )}
    </GoogleMap>
  );
}

export function BusinessMapView({ businesses, center, className }: BusinessMapViewProps) {
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className={`bg-muted rounded-xl flex items-center justify-center ${className}`}>
        <p className="text-muted-foreground text-sm">Google Maps not available</p>
      </div>
    );
  }

  return (
    <div className={`rounded-xl overflow-hidden border border-border ${className}`}>
      <BusinessMapViewInner businesses={businesses} center={center} apiKey={GOOGLE_MAPS_API_KEY} />
    </div>
  );
}
